/**
 * The GS1 standard check digit — General Specifications 26.0 §7.9.1.
 *
 * One algorithm serves GTIN-8/12/13/14, SSCC, GLN, GSRN, GSIN, GDTI, GCN and
 * GRAI. It is defined by *position from the right*, not by field length, which
 * is why a single length-agnostic implementation is correct for all of them and
 * a per-length table would be a bug waiting to happen.
 *
 * Weighting: counting leftwards from the check digit, the first payload digit
 * is multiplied by 3, the next by 1, alternating. The check digit is whatever
 * makes the total a multiple of 10.
 *
 * Worked example, GTIN-13 `9506000134376` (payload `950600013437`, check `6`):
 *
 *   digit   7  3  4  3  1  0  0  0  6  0  5  9    (right to left)
 *   weight  3  1  3  1  3  1  3  1  3  1  3  1
 *   product 21 3  12 3  3  0  0  0  18 0  15 9  = 84
 *   check   (10 - (84 mod 10)) mod 10 = 6
 *
 * The outer `mod 10` matters: without it a payload summing to a multiple of 10
 * yields 10 rather than 0.
 */
import { GS1Error } from "./errors.js";
const isDigits = (s) => {
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        if (c < 48 || c > 57)
            return false;
    }
    return s.length > 0;
};
/**
 * Compute the check digit for a payload that does **not** include one.
 *
 * @param payload - the digits preceding the check digit position
 * @returns the check digit, 0-9
 * @throws {GS1Error} `NOT_NUMERIC` if the payload is empty or non-numeric
 */
export function computeCheckDigit(payload) {
    if (!isDigits(payload)) {
        throw new GS1Error("NOT_NUMERIC", `Check digit input must be one or more digits, received ${JSON.stringify(payload)}.`);
    }
    let sum = 0;
    // i counts leftwards from the check digit position, so i = 0 is the digit
    // immediately left of it and takes weight 3.
    for (let i = 0; i < payload.length; i++) {
        const digit = payload.charCodeAt(payload.length - 1 - i) - 48;
        sum += i % 2 === 0 ? digit * 3 : digit;
    }
    return (10 - (sum % 10)) % 10;
}
/**
 * Check a complete identifier whose final character is its check digit.
 *
 * Returns a boolean rather than throwing because both outcomes are ordinary:
 * a mistyped GTIN is the single most common error surfaced to users, not an
 * exceptional condition.
 */
export function hasValidCheckDigit(value) {
    if (!isDigits(value) || value.length < 2)
        return false;
    const payload = value.slice(0, -1);
    const actual = value.charCodeAt(value.length - 1) - 48;
    return computeCheckDigit(payload) === actual;
}
/**
 * Append the correct check digit to a payload.
 *
 * Used when building identifiers and by the property tests, which need a source
 * of valid identifiers that does not depend on the validator agreeing with
 * itself.
 */
export function withCheckDigit(payload) {
    return payload + String(computeCheckDigit(payload));
}
//# sourceMappingURL=check-digit.js.map